<template>
  <div
    class="menu-container"
    v-if="user"
  >
    <transition name="fade">
      <div
        class="menu-container-top"
        v-if="!channelListIsOpen"
      >
        <transition-group name="fade">
          <div
            class="menu-buttons-container"
            :class="route.name"
          >
            <rc-logout-button v-if="route.name === 'init'"></rc-logout-button>
            <div
              class="previous-route-button-container click"
              v-if="route.name !== 'offices'"
              @click="changeRoute"
            >
              <div class="arrow-left-container">
                <rc-svg
                  class="arrow-left"
                  name="left"
                ></rc-svg>
              </div>
              <span>{{ $t('btn.back', native) }}</span>
            </div>
          </div>
        </transition-group>
      </div>
    </transition>
    <div
      class="menu-item-container hide-on-short-devices"
      @click="goToRoadbookPro"
    >
      <rc-svg name="edit_space"></rc-svg><span>{{ $t('toogle_pro.edit_space-menu-item') }}</span>
    </div>
    <div class="menu-separator-container hide-on-short-devices"></div>
    <div
      class="menu-item-container"
      @click="goToRoadbookPro"
    >
      <rc-svg name="support"></rc-svg><span>{{ $t('toogle_pro.support-menu-item') }}</span>
    </div>
    <div class="menu-separator-container"></div>
    <!-- <div
      class="menu-item-container"
      @click="goToQrcode"
    >
      <rc-svg name="qrcode"></rc-svg><span>{{ $t('toogle_pro.qr_code-menu-item') }}</span>
    </div>
    <div class="menu-separator-container"></div> -->
    <div
      class="menu-item-container"
      @click="openLogoutModal"
    >
      <rc-svg name="logout"></rc-svg><span>{{ $t('btn.quit') }}</span>
    </div>
    <div class="menu-separator-container"></div>
    <div
      class="menu-user-account-container"
      @click="gotoMyAccount"
    >
      <img
        class="user-account-avatar round"
        :src="user?.image_url || user.image_url || defaultImage"
      />
      <rc-poppers
        class="poppers"
        :type="'text'"
        :content="`${user.first_name} ${user.last_name}`"
      ></rc-poppers>
    </div>
    <rc-alert
      :show="isOpen"
      :title-text="$t('logout.title', native)"
      :icon-name="'off'"
      :description="
        $t('logout_prompt', native) +
        ' &lt;/br>&lt;b>' +
        $t('logout.prompt_span', native) +
        '&lt;/b>'
      "
      :footer-confirm-button-text="$t('btn.validate', native)"
      :footer-cancel-button-text="$t('btn.cancel', native)"
      :icon-large="false"
      :icon-stroke="true"
      @confirm="logout"
      @cancel="closeLogoutModal"
    ></rc-alert>
  </div>
</template>

<script setup lang="ts">
  import RcSvg from '@/components/medias/RcSvg.vue';
  import RcLogoutButton from '@/components/inputs/RcLogoutButton.vue';
  import RcAlert from '@/components/feedbacks/RcAlert.vue';
  import RcPoppers from '@/components/data_displays/RcPoppers.vue';
  import defaultImage from '@/assets/avatar-default-man.svg';
  import { useMixin } from '@/utils/mixins/useMixin.mixin';
  import { useUserStore } from '@/store';
  import { useRoute, useRouter } from 'vue-router';
  import type { RouteLocationRaw } from 'vue-router';
  import { ref, computed } from 'vue';

  const router = useRouter();
  const route = useRoute();
  const { native } = useMixin();

  const userStore = useUserStore();

  // Data
  const channelListIsOpen = ref<boolean>(false);
  const isOpen = ref<boolean>(false);

  // Computed
  const user = computed(() => userStore.user);

  // Methods
  const changeRoute = (): void => {
    if (typeof route.meta.previous == 'undefined') {
      router.push('/offices');
    } else {
      router.push(route.meta.previous as RouteLocationRaw);
    }
  };

  function goToRoadbookPro() {
    if (window.location.origin.includes('localhost')) window.open(`http://localhost:3006/#/`);
    if (window.location.origin.includes('rc-preprod'))
      window.open('https://pro.roadbook.rc-preprod.com/#/');
    if (window.location.origin.includes('app.roadbook.travel'))
      window.open('https://pro.roadbook.travel/#/');
  }

  // const goToQrcode = (): void => {
  //   router.push('/qr-code');
  // };

  const gotoMyAccount = (): void => {
    router.push('/advisors');
  };

  const openLogoutModal = (): void => {
    isOpen.value = true;
  };

  const closeLogoutModal = (): void => {
    isOpen.value = false;
  };

  const logout = async (): Promise<void> => {
    userStore.logout();
  };
</script>

<style lang="postcss">
  .menu-container {
    @apply flex flex-col w-full items-center justify-end h-full;

    .menu-container-top {
      @apply mb-auto flex flex-col items-center w-full;

      .menu-buttons-container {
        @apply w-full py-[15px] px-0 rounded-tl-lg box-border flex justify-center items-center flex-col bg-white whitespace-nowrap;
        row-gap: 15px;

        .previous-route-button-container {
          @apply flex flex-col items-center justify-center gap-2 w-full transition duration-300 text-lg leading-4 font-medium;

          .arrow-left-container {
            @apply border border-solid border-grey-middle rounded-[99px] h-[26px] w-[26px] opacity-80 flex items-center justify-center outline-[0.5px] outline-transparent transition duration-500 ease-in-out;

            &:hover {
              @apply outline-[0.5px] outline-grey-middle opacity-100;
            }

            .arrow-left {
              @apply text-[45px];
            }

            svg {
              width: 6px !important;
              @apply mr-2.5 align-middle box-content;

              path {
                stroke: theme('colors.grey-middle') !important;
                @apply stroke-[4px];
              }
            }
          }

          span {
            @apply text-[11px] font-light text-center overflow-hidden text-ellipsis;

            @media screen and (max-height: 640px) {
              @apply hidden;
            }
          }
        }
      }
    }

    .menu-item-container {
      @apply w-full flex flex-col items-center justify-center gap-3 py-5 px-3 text-primary cursor-pointer;

      @media screen and (max-height: 768px) {
        @apply p-4 gap-2.5;
      }

      .svg-icon {
        @apply fill-primary h-6;
      }

      span {
        @apply text-[10px] font-normal text-center overflow-hidden text-ellipsis leading-3;

        @media screen and (max-height: 640px) {
          @apply hidden;
        }
      }

      &:hover {
        @apply text-secondary transition-colors;

        .svg-icon {
          @apply fill-secondary transition-colors;
        }
      }
    }

    .menu-separator-container {
      @apply bg-grey-middle opacity-40 w-10 h-[1px] min-h-[1px];
    }

    .hide-on-short-devices {
      @media (min-width: 1301px) {
        @apply hidden bg-red-500;
      }
    }

    .menu-user-account-container {
      @apply flex flex-col w-full items-center cursor-pointer mt-5;

      @media screen and (max-height: 768px) {
        @apply mt-4;
      }

      .user-account-avatar {
        @apply w-10 h-10 border-2 border-solid border-grey-middle cursor-pointer;

        &:hover {
          @apply border-primary;
        }
      }
    }
  }
</style>
